<script setup>
import { onMounted, ref, watch } from 'vue';
import { message, Upload, Button, Image, Empty, Popconfirm, Spin } from 'ant-design-vue';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons-vue';
import {
    listWhiteboardPhotos,
    uploadWhiteboardPhoto,
    deleteWhiteboardPhoto,
    fetchWhiteboardPhotoBlob
} from '@/api/finance-version/whiteboardPhotos';

const props = defineProps({
    balanceId: { type: [Number, String], required: true },
    readonly: { type: Boolean, default: false }
});

const loading = ref(false);
const uploading = ref(false);
const photos = ref([]);
const previewMap = ref({});
const previewFailed = ref({});

const revokePreview = (photoId) => {
    const url = previewMap.value[photoId];
    if (url) URL.revokeObjectURL(url);
};

const loadPreview = async (photo) => {
    if (previewMap.value[photo.id] || previewFailed.value[photo.id]) return;
    try {
        const blob = await fetchWhiteboardPhotoBlob(props.balanceId, photo.id);
        if (!(blob instanceof Blob) || blob.size === 0) {
            throw new Error('empty');
        }
        // 若後端錯誤回 JSON，Content-Type 不是 image
        if (blob.type && blob.type.includes('json')) {
            throw new Error('not-image');
        }
        previewMap.value = {
            ...previewMap.value,
            [photo.id]: URL.createObjectURL(blob)
        };
    } catch {
        previewFailed.value = { ...previewFailed.value, [photo.id]: true };
    }
};

const load = async () => {
    if (!props.balanceId) return;
    loading.value = true;
    try {
        Object.keys(previewMap.value).forEach((id) => revokePreview(Number(id)));
        previewMap.value = {};
        previewFailed.value = {};
        const res = await listWhiteboardPhotos(props.balanceId);
        if (res?.status !== 1) throw new Error(res?.message || '載入失敗');
        photos.value = res.data?.list || [];
        await Promise.all(photos.value.map((p) => loadPreview(p)));
    } catch (e) {
        message.error(e.message || '白板照片載入失敗');
    } finally {
        loading.value = false;
    }
};

const beforeUpload = async (file) => {
    if (props.readonly) return Upload.LIST_IGNORE;
    const okType = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
    if (!okType) {
        message.error('僅支援 JPG／PNG／WebP');
        return Upload.LIST_IGNORE;
    }
    if (file.size > 10 * 1024 * 1024) {
        message.error('單張上限 10MB');
        return Upload.LIST_IGNORE;
    }
    uploading.value = true;
    try {
        const res = await uploadWhiteboardPhoto(props.balanceId, file);
        if (res?.status !== 1) throw new Error(res?.message || '上傳失敗');
        message.success('已上傳白板照片');
        await load();
    } catch (e) {
        const status = e?.response?.status || e?.status;
        const raw = String(e?.message || '');
        if (status === 413 || /413|Payload Too Large|too large/i.test(raw)) {
            message.error('圖片太大（單張請小於 10MB），或伺服器上傳上限過低');
        } else {
            message.error(e.message || '上傳失敗');
        }
    } finally {
        uploading.value = false;
    }
    return Upload.LIST_IGNORE;
};

const remove = async (photo) => {
    try {
        const res = await deleteWhiteboardPhoto(props.balanceId, photo.id);
        if (res?.status !== 1) throw new Error(res?.message || '刪除失敗');
        revokePreview(photo.id);
        message.success('已刪除');
        await load();
    } catch (e) {
        message.error(e.message || '刪除失敗');
    }
};

watch(
    () => props.balanceId,
    () => load(),
    { immediate: false }
);
onMounted(load);
</script>

<template>
    <section class="wb-panel">
        <header class="wb-panel__head">
            <strong>諮詢白板照片</strong>
            <Upload
                v-if="!readonly"
                :show-upload-list="false"
                accept="image/jpeg,image/png,image/webp"
                :before-upload="beforeUpload"
                :disabled="uploading"
            >
                <Button type="primary" :loading="uploading">
                    <template #icon><PlusOutlined /></template>
                    上傳照片
                </Button>
            </Upload>
        </header>

        <Spin :spinning="loading">
            <div v-if="photos.length" class="wb-grid">
                <figure v-for="photo in photos" :key="photo.id" class="wb-card">
                    <Image
                        v-if="previewMap[photo.id]"
                        :src="previewMap[photo.id]"
                        :alt="photo.original_name || 'whiteboard'"
                        class="wb-card__img"
                    />
                    <div v-else-if="previewFailed[photo.id]" class="wb-card__placeholder">
                        預覽失敗
                    </div>
                    <div v-else class="wb-card__placeholder">載入中…</div>
                    <figcaption>
                        <span :title="photo.original_name">{{
                            photo.original_name || `照片 #${photo.id}`
                        }}</span>
                        <Popconfirm
                            v-if="!readonly"
                            title="刪除此白板照片？"
                            ok-text="刪除"
                            cancel-text="取消"
                            @confirm="remove(photo)"
                        >
                            <Button type="text" danger size="small">
                                <DeleteOutlined />
                            </Button>
                        </Popconfirm>
                    </figcaption>
                </figure>
            </div>
            <Empty v-else description="尚未上傳白板照片" :image="Empty.PRESENTED_IMAGE_SIMPLE" />
        </Spin>
    </section>
</template>

<style scoped>
.wb-panel {
    margin: 14px 0 18px;
    padding: 12px 14px;
    border: 1px solid #d0d5dd;
    border-radius: 10px;
    background: #fff;
}
.wb-panel__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}
.wb-panel__head strong {
    font-size: 14px;
    color: #101828;
}
.wb-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
}
.wb-card {
    margin: 0;
    overflow: hidden;
    border: 1px solid #e4e7ec;
    border-radius: 8px;
    background: #f9fafb;
}
.wb-card__img {
    display: block;
    width: 100%;
    height: 120px;
    object-fit: cover;
}
.wb-card__placeholder {
    display: grid;
    place-items: center;
    height: 120px;
    color: #98a2b3;
    font-size: 12px;
}
.wb-card figcaption {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    padding: 6px 8px;
    font-size: 12px;
    color: #475467;
}
.wb-card figcaption span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
